// client/src/pages/ApprovalConsole.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import API from '../api/client';
import {
    Box, Typography, Button, Checkbox, Snackbar, Skeleton, IconButton,
    Divider, Grid, Alert, Avatar, Chip, CircularProgress
} from '@mui/material';
import {
    Search, SlidersHorizontal, CornerDownRight, ArrowRight,
    ArrowUpDown, X, Check, Inbox, AlertTriangle, TrendingUp,
    Clock, Calendar
} from 'lucide-react';

const ApprovalConsole = ({ role }) => {
    // 💡 Fix: Normalize incoming role props to match lowercase architecture safely
    const cleanRole = String(role).trim().toLowerCase();

    // Core Ledger Data States
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [bulkLoading, setBulkLoading] = useState(false);
    const [error, setError] = useState('');
    const [remarks, setRemarks] = useState({});

    // Filter, Navigation, & Selection States
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedIds, setSelectedIds] = useState([]);

    // Global Toast Notification State
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // 💡 Fix: Wrapped in useCallback to prevent infinite rerender loops and satisfy ESLint rules
    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get('/leave/summary');
            const allRequests = response.data?.data || response.data || [];

            // 💡 Fix: Standardized to match lowercase normalized roles
            const actionableRequests = allRequests.filter(req => {
                if (cleanRole === 'hr') return req.hr_status === 'Pending';
                if (cleanRole === 'manager') return req.hr_status === 'Approved' && req.manager_status === 'Pending';
                return true;
            });

            setRequests(actionableRequests);
            setSelectedIds([]); // Clear selections on refresh
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to sync with internal operations ledger.');
        } finally {
            setLoading(false);
        }
    }, [cleanRole]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]); // 💡 Fix: Safely included fetchRequests as a stable dependency

    /**
     * 💡 Fix: Transformed into a clean, side-effect-free data fabric return pipeline.
     * Prevents rendering crash sequences entirely.
     */
    const filteredRequests = useMemo(() => {
        let items = [...requests];

        if (searchQuery) {
            items = items.filter(req => String(req.employee_id).toLowerCase().includes(searchQuery.toLowerCase()));
        }
        if (typeFilter !== 'All') {
            items = items.filter(req => req.leave_type === typeFilter);
        }

        items.sort((a, b) => {
            let comparison = 0;
            if (sortBy === 'date') {
                comparison = new Date(a.start_date) - new Date(b.start_date);
            } else if (sortBy === 'employee') {
                comparison = String(a.employee_id).localeCompare(String(b.employee_id));
            }
            return sortOrder === 'desc' ? -comparison : comparison;
        });

        return items;
    }, [searchQuery, typeFilter, sortBy, sortOrder, requests]);

    // Derived High-Value Trend Metrics
    const derivedMetrics = useMemo(() => {
        if (!requests.length) return { topType: 'None', cumulativeDays: 0, criticalCount: 0, urgentRequest: null };

        let totalDays = 0;
        let critical = 0;
        const typeCounts = {};
        let highestImpactRequest = null;
        let maxImpactDays = 0;

        requests.forEach(req => {
            const days = Math.ceil(Math.abs(new Date(req.end_date) - new Date(req.start_date)) / (1000 * 60 * 60 * 24)) + 1;
            totalDays += days;

            typeCounts[req.leave_type] = (typeCounts[req.leave_type] || 0) + 1;

            const daysUntilStart = Math.ceil((new Date(req.start_date) - new Date()) / (1000 * 60 * 60 * 24));
            const isUrgentTimeline = daysUntilStart <= 2 && daysUntilStart >= -1;

            if (days > 5 || isUrgentTimeline) {
                critical++;
            }

            if (days > maxImpactDays) {
                maxImpactDays = days;
                highestImpactRequest = req;
            }
        });

        const topType = Object.keys(typeCounts).reduce((a, b) => typeCounts[a] > typeCounts[b] ? a : b, 'None');

        return {
            topType,
            cumulativeDays: totalDays,
            criticalCount: critical,
            urgentRequest: highestImpactRequest
        };
    }, [requests]);

    // Individual Approval / Denial Actions
    const handleAction = async (id, targetStatus) => {
        try {
            setActionLoading(prev => ({ ...prev, [id]: true }));
            const endpoint = cleanRole === 'manager' ? '/leave/manager-action' : '/leave/hr-action';

            await API.patch(endpoint, {
                id,
                status: targetStatus,
                remark: remarks[id] || ''
            });

            setRemarks(prev => ({ ...prev, [id]: '' }));
            setExpandedRow(null);
            setSnackbar({ open: true, message: `Request successfully ${targetStatus.toLowerCase()}.`, severity: 'success' });
            await fetchRequests();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.error || 'Authorization lifecycle update failed.', severity: 'error' });
        } finally {
            setActionLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    // Parallel Batch Processing for Selected Elements
    const handleBulkAction = async (targetStatus) => {
        try {
            setBulkLoading(true);
            const endpoint = cleanRole === 'manager' ? '/leave/manager-action' : '/leave/hr-action';

            await Promise.all(selectedIds.map(id =>
                API.patch(endpoint, {
                    id,
                    status: targetStatus,
                    remark: 'Bulk processed via management console.'
                })
            ));

            setSnackbar({ open: true, message: `Successfully executed bulk ${targetStatus.toLowerCase()} action for ${selectedIds.length} items.`, severity: 'success' });
            setSelectedIds([]);
            await fetchRequests();
        } catch (err) {
            setSnackbar({ open: true, message: 'One or more bulk updates could not process correctly.', severity: 'error' });
        } finally {
            setBulkLoading(false);
        }
    };

    const handleSortToggle = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(filteredRequests.map(r => r.id));
        } else {
            setSelectedIds([]);
        }
    };

    const toggleSelectRow = (e, id) => {
        e.stopPropagation();
        setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const calculateDays = (start, end) => {
        return Math.ceil(Math.abs(new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
    };

    const uniqueLeaveTypes = ['All', ...new Set(requests.map(r => r.leave_type))];

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', px: { xs: 3, md: 6 }, py: 6 }}>
                <Box sx={{ mx: 'auto' }}>
                    <Skeleton variant="text" width="140px" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="320px" height={48} sx={{ mb: 6 }} />
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '8px', mb: 4 }} />
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Skeleton variant="rectangular" width={240} height={32} sx={{ borderRadius: '6px' }} />
                        <Skeleton variant="rectangular" width={140} height={32} sx={{ borderRadius: '6px' }} />
                    </Box>
                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: '8px' }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', color: '#171717', px: { xs: 3, md: 4 }, py: 1, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
            <Box sx={{ mx: 'auto' }}>

                {/* Floating Contextual Bulk Action Deck */}
                {selectedIds.length > 0 && (
                    <Box sx={{
                        position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
                        bgcolor: '#171717', color: '#ffffff', px: 3, py: 1.5, borderRadius: '12px',
                        display: 'flex', alignItems: 'center', gap: 3, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
                        zIndex: 1000, transition: 'all 0.2s ease-in-out', border: '1px solid #262626'
                    }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                            {selectedIds.length} entry selected
                        </Typography>
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: '#404040', my: 0.5 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small" variant="text" disabled={bulkLoading} onClick={() => handleBulkAction('Rejected')}
                                sx={{ color: '#ef4444', textTransform: 'none', fontSize: '12px', fontWeight: 600, '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                            >
                                Deny Selected
                            </Button>
                            <Button
                                size="small" variant="text" disabled={bulkLoading} onClick={() => handleBulkAction('Approved')}
                                sx={{ color: '#10b981', textTransform: 'none', fontSize: '12px', fontWeight: 600, '&:hover': { bgcolor: 'rgba(16,185,129,0.1)' } }}
                            >
                                Approve Selected
                            </Button>
                        </Box>
                        <IconButton size="small" onClick={() => setSelectedIds([])} sx={{ color: '#a3a3a3', p: 0.5 }}>
                            <X size={14} />
                        </IconButton>
                    </Box>
                )}

                {/* Linear Header Architecture */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pb: 4, mb: 4, borderBottom: '1px solid #e5e5e5' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: '#737373', fontSize: '13px', letterSpacing: '-0.01em' }}>
                                Authorization Engine
                            </Typography>
                            <span style={{ color: '#d4d4d4', fontSize: '12px' }}>/</span>
                            <Chip
                                label={role === 'HR' ? 'HR Ledger' : 'Management Audit'}
                                size="small"
                                sx={{ height: 20, fontSize: '11px', fontWeight: 600, bgcolor: '#e5e5e5', color: '#171717', borderRadius: '4px' }}
                            />
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000000', letterSpacing: '-0.03em' }}>
                            Action Required
                        </Typography>
                    </Box>

                    {/* Inline Telemetry Counters */}
                    <Box sx={{ display: 'flex', gap: 4, mb: 0.5 }}>
                        <Box sx={{ textTransform: 'uppercase' }}>
                            <Typography sx={{ fontSize: '11px', color: '#737373', fontWeight: 500, letterSpacing: '0.05em' }}>Total Queue</Typography>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>{requests.length}</Typography>
                        </Box>
                        <Box sx={{ textTransform: 'uppercase' }}>
                            <Typography sx={{ fontSize: '11px', color: '#737373', fontWeight: 500, letterSpacing: '0.05em' }}>Matches</Typography>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#171717' }}>{filteredRequests.length}</Typography>
                        </Box>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '6px', border: '1px solid #fca5a5', bgcolor: '#fef2f2', color: '#991b1b', fontSize: '13px' }}>{error}</Alert>}

                {requests.length > 0 && (
                    <>
                        {/* Trends & Performance Signals Deck */}
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={4} md={3}>
                                <Box sx={{ bgcolor: '#ffffff', p: 2, borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, color: '#737373' }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', flexGrow: 1 }}>Top Vector Type</Typography>
                                        <TrendingUp size={14} style={{ color: '#a3a3a3' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {derivedMetrics.topType}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Box sx={{ bgcolor: '#ffffff', p: 2, borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, color: '#737373' }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', flexGrow: 1 }}>Total Pipeline Load</Typography>
                                        <Calendar size={14} style={{ color: '#a3a3a3' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#171717' }}>
                                        {derivedMetrics.cumulativeDays} Days Ordered
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Box sx={{ bgcolor: '#ffffff', p: 2, borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, color: '#737373' }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', flexGrow: 1 }}>Attention Required</Typography>
                                        <Clock size={14} style={{ color: '#f59e0b' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: derivedMetrics.criticalCount > 0 ? '#b45309' : '#171717' }}>
                                        {derivedMetrics.criticalCount} High Impact Cases
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Dynamic Actionable Insight Panel */}
                            <Grid item xs={12} md={3}>
                                {derivedMetrics.urgentRequest ? (
                                    <Box
                                        onClick={() => setExpandedRow(derivedMetrics.urgentRequest.id)}
                                        sx={{
                                            bgcolor: '#020202', p: 2, borderRadius: '8px', border: '1px solid #fde68a',
                                            cursor: 'pointer', height: '58%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                            transition: 'border-color 0.15s ease', '&:hover': { borderColor: '#d97706' }
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#d80a0a', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AlertTriangle size={10} /> Priority Focus Highlight
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#f3efed' }}>
                                            EE-{derivedMetrics.urgentRequest.employee_id} ({calculateDays(derivedMetrics.urgentRequest.start_date, derivedMetrics.urgentRequest.end_date)}d window)
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ bgcolor: '#ffffff', p: 2, borderRadius: '8px', border: '1px solid #e5e5e5', display: 'flex', alignItems: 'center', height: '100%' }}>
                                        <Typography sx={{ fontSize: '12px', color: '#737373' }}>No systemic priority triggers flagged.</Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}

                {requests.length === 0 ? (
                    <Box sx={{ py: 12, textAlign: 'center', border: '1px dashed #e5e5e5', borderRadius: '8px', bgcolor: '#ffffff' }}>
                        <Inbox size={28} style={{ color: '#a3a3a3', margin: '0 auto 12px' }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#171717' }}>Inbox Cleaned</Typography>
                        <Typography sx={{ color: '#737373', fontSize: '13px', mt: 0.5 }}>All entries verified in your current tracking cycle.</Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* System Filtering Panel Strip */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center' }}>
                            <Box sx={{ position: 'relative', width: '260px' }}>
                                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#a3a3a3', zIndex: 2 }} />
                                <input
                                    type="text"
                                    placeholder="Filter by Employee ID..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        width: '100%', padding: '6px 10px 6px 32px', fontSize: '13px', fontWeight: 500,
                                        backgroundColor: '#ffffff', color: '#171717', border: '1px solid #e5e5e5',
                                        borderRadius: '6px', outline: 'none', transition: 'border-color 0.15s ease'
                                    }}
                                />
                            </Box>

                            <Box sx={{ position: 'relative' }}>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    style={{
                                        padding: '6px 28px 6px 12px', fontSize: '13px', fontWeight: 500,
                                        backgroundColor: '#ffffff', color: '#171717', border: '1px solid #e5e5e5',
                                        borderRadius: '0 6px 6px 0', outline: 'none', appearance: 'none', cursor: 'pointer'
                                    }}
                                >
                                    {uniqueLeaveTypes.map(t => <option key={t} value={t}>{t === 'All' ? 'All Classifications' : t}</option>)}
                                </select>
                                <SlidersHorizontal size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#a3a3a3', pointerEvents: 'none' }} />
                            </Box>
                        </Box>

                        {/* Interactive Data Grid Shell */}
                        <Box sx={{ border: '1px solid #e5e5e5', borderRadius: '8px', bgcolor: '#ffffff', overflow: 'hidden' }}>

                            {/* Static Sorting Headers Area */}
                            <Box sx={{ display: 'flex', px: 2, py: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid #e5e5e5', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', width: '4%', pl: 0.5 }}>
                                    <Checkbox
                                        size="small"
                                        checked={filteredRequests.length > 0 && selectedIds.length === filteredRequests.length}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredRequests.length}
                                        onChange={handleSelectAll}
                                        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                                    />
                                </Box>
                                <Box sx={{ width: '22%', display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSortToggle('employee')}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#737373', letterSpacing: '0.05em' }}>APPLICANT ID</Typography>
                                    <ArrowUpDown size={11} style={{ marginLeft: 6, color: sortBy === 'employee' ? '#171717' : '#a3a3a3' }} />
                                </Box>
                                <Box sx={{ width: '20%' }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#737373', letterSpacing: '0.05em' }}>TYPE</Typography>
                                </Box>
                                <Box sx={{ width: '36%', display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSortToggle('date')}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#737373', letterSpacing: '0.05em' }}>CALENDAR PERIOD</Typography>
                                    <ArrowUpDown size={11} style={{ marginLeft: 6, color: sortBy === 'date' ? '#171717' : '#a3a3a3' }} />
                                </Box>
                                <Box sx={{ width: '18%', display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#737373', letterSpacing: '0.05em' }}>STATE FLAGS</Typography>
                                </Box>
                            </Box>

                            {/* Dynamic Rows Container */}
                            {filteredRequests.map((request) => {
                                const isExpanded = expandedRow === request.id;
                                const totalDays = calculateDays(request.start_date, request.end_date);
                                const isRowSelected = selectedIds.includes(request.id);

                                return (
                                    <Box
                                        key={request.id}
                                        sx={{
                                            borderBottom: '1px solid #e5e5e5',
                                            bgcolor: isExpanded ? '#fafafa' : isRowSelected ? '#fcfcfc' : '#ffffff',
                                            '&:last-child': { borderBottom: 'none' }
                                        }}
                                    >
                                        {/* Core Data Row Content */}
                                        <Box
                                            onClick={() => setExpandedRow(isExpanded ? null : request.id)}
                                            sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', transition: 'background-color 0.1s ease', '&:hover': { bgcolor: '#fafafa' } }}
                                        >
                                            {/* Checkbox Capture Unit */}
                                            <Box sx={{ width: '4%', display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    size="small"
                                                    checked={isRowSelected}
                                                    onChange={(e) => toggleSelectRow(e, request.id)}
                                                    sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                                                />
                                            </Box>

                                            {/* Employee ID Matrix */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '22%' }}>
                                                <Avatar sx={{ bgcolor: '#171717', width: 22, height: 22, fontSize: '10px', fontWeight: 600 }}>
                                                    {String(request.employee_id).slice(-2)}
                                                </Avatar>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#171717' }}>
                                                    EE-{request.employee_id}
                                                </Typography>
                                            </Box>

                                            {/* Classification Parameters */}
                                            <Box sx={{ width: '20%' }}>
                                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#525252' }}>
                                                    {request.leave_type}
                                                </Typography>
                                            </Box>

                                            {/* Date Execution Spans */}
                                            <Box sx={{ width: '36%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#171717' }}>
                                                    {new Date(request.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </Typography>
                                                <ArrowRight size={12} style={{ color: '#a3a3a3' }} />
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#171717' }}>
                                                    {new Date(request.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </Typography>
                                                <span style={{ fontSize: '12px', color: '#737373', fontWeight: 400 }}>({totalDays}d)</span>
                                            </Box>

                                            {/* State Workflow Chips */}
                                            <Box sx={{ width: '18%', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                <Chip
                                                    label={`HR: ${request.hr_status}`} size="small"
                                                    sx={{
                                                        height: 18, fontSize: '10px', fontWeight: 600,
                                                        bgcolor: request.hr_status === 'Approved' ? '#ecfdf5' : '#f5f5f5',
                                                        color: request.hr_status === 'Approved' ? '#047857' : '#525252', borderRadius: '4px'
                                                    }}
                                                />
                                                <Chip
                                                    label={`MGR: ${request.manager_status}`} size="small"
                                                    sx={{
                                                        height: 18, fontSize: '10px', fontWeight: 600,
                                                        bgcolor: request.manager_status === 'Approved' ? '#ecfdf5' : '#f5f5f5',
                                                        color: request.manager_status === 'Approved' ? '#047857' : '#525252', borderRadius: '4px'
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Internal Evaluation Drawer Component */}
                                        {isExpanded && (
                                            <Box sx={{ px: 3, pb: 3, pt: 1, borderTop: '1px dashed #e5e5e5', bgcolor: '#ffffff' }}>
                                                <Grid container spacing={4} sx={{ mt: 0.5 }}>

                                                    {/* Justification Segment */}
                                                    <Grid item xs={12} md={7}>
                                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                            <CornerDownRight size={14} style={{ color: '#a3a3a3', marginTop: 3, flexShrink: 0 }} />
                                                            <Box>
                                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                                                    Filing Rationale Justification Statement
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '13px', color: '#404040', lineHeight: 1.5 }}>
                                                                    {request.reason || "No explicit reasoning details appended to this transaction."}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {request.hr_remark && role === 'Manager' && (
                                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2.5, pl: 3.5 }}>
                                                                <Box>
                                                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                                                        HR Historical Verification Logs
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '13px', color: '#737373', fontStyle: 'italic' }}>
                                                                        "{request.hr_remark}"
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Grid>

                                                    {/* Decisions Execution Segment */}
                                                    <Grid item xs={12} md={5} sx={{ borderLeft: { md: '1px solid #f5f5f5' }, pl: { md: 4 } }}>
                                                        {totalDays > 5 && (
                                                            <Box sx={{ p: 1, mb: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <AlertTriangle size={12} style={{ color: '#b45309', flexShrink: 0 }} />
                                                                <Typography sx={{ fontSize: '11px', color: '#b45309', fontWeight: 500 }}>
                                                                    Extended window requires resource buffer checking.
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                                                            Internal Review Notation
                                                        </Typography>

                                                        <textarea
                                                            rows={2}
                                                            placeholder="Add ledger review trail notes..."
                                                            value={remarks[request.id] || ''}
                                                            onChange={(e) => setRemarks(prev => ({ ...prev, [request.id]: e.target.value }))}
                                                            style={{
                                                                width: '100%', padding: '8px 10px', fontSize: '13px',
                                                                backgroundColor: '#fafafa', color: '#171717', border: '1px solid #e5e5e5',
                                                                borderRadius: '6px', outline: 'none', resize: 'none', fontFamily: 'inherit'
                                                            }}
                                                        />

                                                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 2 }}>
                                                            <Button
                                                                size="small"
                                                                disabled={actionLoading[request.id]}
                                                                onClick={() => handleAction(request.id, 'Rejected')}
                                                                startIcon={!actionLoading[request.id] && <X size={13} />}
                                                                sx={{
                                                                    textTransform: 'none', color: '#dc2626', fontSize: '12px', fontWeight: 600,
                                                                    px: 2, py: 0.5, borderRadius: '6px', border: '1px solid #fca5a5', bgcolor: '#ffffff',
                                                                    '&:hover': { bgcolor: '#fef2f2' }, '&:disabled': { opacity: 0.5 }
                                                                }}
                                                            >
                                                                {actionLoading[request.id] ? <CircularProgress size={12} color="inherit" /> : 'Deny'}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                disabled={actionLoading[request.id]}
                                                                onClick={() => handleAction(request.id, 'Approved')}
                                                                startIcon={!actionLoading[request.id] && <Check size={13} />}
                                                                sx={{
                                                                    textTransform: 'none', bgcolor: '#171717', color: '#ffffff', fontSize: '12px', fontWeight: 600,
                                                                    px: 2, py: 0.5, borderRadius: '6px', boxShadow: 'none',
                                                                    '&:hover': { bgcolor: '#262626' }, '&:disabled': { opacity: 0.5 }
                                                                }}
                                                            >
                                                                {actionLoading[request.id] ? <CircularProgress size={12} color="inherit" /> : 'Approve'}
                                                            </Button>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* Global Message System */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    message={snackbar.message}
                />
            </Box>
        </Box>
    );
};

export default ApprovalConsole;